<template>
  <div class="dd-page">
    <AppBar />

    <div class="dd-container">
      <!-- Page header -->
      <div class="dd-header">
        <button class="btn btn--ghost btn--sm dd-back" @click="goBack">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Voltar ao guia
        </button>

        <h1 class="dd-title">Envio de documentos</h1>
        <p class="sub dd-sub">
          Para completar seu cadastro, precisamos de alguns documentos. Envie os
          arquivos abaixo em formato de imagem (JPG, PNG) ou PDF.
        </p>

        <!-- Progress bar -->
        <div class="dd-progress">
          <div class="dd-progress__bar">
            <div
              class="dd-progress__fill"
              :style="{ width: progressPercentage + '%' }"
            />
          </div>
          <span class="dd-progress__label">
            {{ sentDocumentsCount }}/{{ requiredDocumentsCount }} enviados
          </span>
        </div>
      </div>

      <!-- Tabs: enviar documentos / documentos enviados -->
      <div class="tabs">
        <span
          class="tab"
          :class="{ 'tab--active': activeTab === 'send' }"
          style="cursor:pointer"
          @click="activeTab = 'send'"
        >
          Enviar documentos
        </span>
        <span
          class="tab"
          :class="{ 'tab--active': activeTab === 'sent' }"
          style="cursor:pointer"
          @click="activeTab = 'sent'"
        >
          Documentos enviados
        </span>
      </div>

      <div v-if="activeTab === 'send'" class="dd-grid">
        <div
          v-for="docType in documentTypes"
          :key="docType.type"
          class="card dd-card"
          :class="{
            'dd-card--done': getDocumentStatus(docType.type) === 'accepted'
          }"
        >
          <!-- Card header -->
          <div class="dd-card__header">
            <span class="dd-card__label">{{ docType.label }}</span>
            <span
              class="badge badge--no-dot"
              :class="statusBadgeClass(docType.type)"
            >
              {{ getStatusLabel(docType.type) }}
            </span>
          </div>

          <!-- Rejection reason -->
          <div
            v-if="getDocumentRejectedReason(docType.type)"
            class="dd-rejection"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M12 8v4M12 16h.01"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <span>{{ getDocumentRejectedReason(docType.type) }}</span>
          </div>

          <!-- Upload area -->
          <DoctorDocumentUploadFields
            v-if="isUploadVisible(docType.type)"
            :has-back="docType.hasBack"
            :loading="loading"
            @submit="handleDocumentSubmit(docType.type, $event)"
          />
        </div>
      </div>

      <DoctorDocumentsList
        v-else
        :documents="documents"
        :document-types="allDocumentTypes"
      />

      <!-- Bottom CTA -->
      <div class="dd-footer">
        <button class="btn btn--outline" @click="goBack">
          Voltar ao guia
        </button>
      </div>
    </div>

    <footer class="foot-mini">
      <router-link to="/" class="vm-mark">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
      </router-link>
      <span class="cpy">© 2026 Videomedi · CNPJ 00.000.000/0001-00</span>
      <div class="links">
        <a href="#">Privacidade</a>
        <a href="#">Termos de Uso</a>
        <a href="#">LGPD</a>
        <a href="#">Suporte</a>
      </div>
    </footer>
  </div>
</template>

<script>
import { doctorDocumentMethods } from "../state/helpers";
import AppBar from "../components/AppBar.vue";
import DoctorDocumentsList from "../components/DoctorDocumentsList.vue";
import DoctorDocumentUploadFields from "../components/DoctorDocumentUploadFields.vue";

export default {
  name: "DoctorDocumentsPage",
  components: { AppBar, DoctorDocumentsList, DoctorDocumentUploadFields },
  data() {
    return {
      activeTab: "send",
      allDocumentTypes: [
        { type: "identity_or_cnh", label: "Identidade ou CNH", hasBack: true },
        { type: "cpf", label: "CPF", hasBack: false },
        { type: "diploma", label: "Diploma", hasBack: false },
        { type: "rqe", label: "RQE", hasBack: false },
        { type: "crm", label: "CRM", hasBack: false },
        { type: "curriculum", label: "Currículo", hasBack: false },
        { type: "mini_curriculum", label: "Mini Currículo", hasBack: false },
        { type: "photo", label: "Foto", hasBack: false },
        {
          type: "vaccination_card",
          label: "Cartão de Vacinação",
          hasBack: false
        },
        {
          type: "training_certificates",
          label: "Certificados de Treinamento",
          hasBack: false
        },
        {
          type: "annual_fee_certificate",
          label: "Certidão de Anuidade",
          hasBack: false
        },
        {
          type: "address_proof",
          label: "Comprovante de Endereço",
          hasBack: false
        },
        {
          type: "specialty_title",
          label: "Título de Especialidade",
          hasBack: false
        },
        {
          type: "ethical_certificate",
          label: "Certidão Ética",
          hasBack: false
        },
        {
          type: "registration_regularity_certificate",
          label: "Certidão de Regularidade",
          hasBack: false
        },
        {
          type: "class_registration_card",
          label: "Carteira de Registro do Conselho",
          hasBack: false
        },
        {
          type: "class_annual_fee_certificate",
          label: "Certidão de Anuidade do Conselho",
          hasBack: false
        },
        {
          type: "specialization_titles",
          label: "Títulos de especialização (opcional)",
          hasBack: false,
          optional: true
        }
      ]
    };
  },
  mounted() {
    this.syncDocumentRequirements(this.currentUser.doctor_property);
    this.fetchDocuments().catch(() => {});
  },
  methods: {
    ...doctorDocumentMethods,

    goBack() {
      this.$router.push({ name: "GuidePage" });
    },

    getExistingDocument(docType) {
      return this.documents.find(doc => doc.document_type === docType);
    },

    getDocumentStatus(docType) {
      const doc = this.getExistingDocument(docType);
      return doc ? doc.status : "pending";
    },

    getStatusLabel(docType) {
      const doc = this.getExistingDocument(docType);
      const config = this.allDocumentTypes.find(d => d.type === docType);
      if (!doc && config && config.optional) return "Opcional";
      const map = {
        pending: "Pendente",
        in_analysis: "Em análise",
        accepted: "Aceito",
        rejected: "Rejeitado"
      };
      return map[this.getDocumentStatus(docType)] || "Pendente";
    },

    statusBadgeClass(docType) {
      const status = this.getDocumentStatus(docType);
      const doc = this.getExistingDocument(docType);
      const config = this.allDocumentTypes.find(d => d.type === docType);
      if (!doc && config && config.optional) return "badge--info";
      return (
        {
          pending: "badge--warning",
          in_analysis: "badge--warning",
          accepted: "badge--success",
          rejected: "badge--danger"
        }[status] || "badge--warning"
      );
    },

    getDocumentRejectedReason(docType) {
      const doc = this.getExistingDocument(docType);
      return doc && doc.status === "rejected" ? doc.rejected_motivation : null;
    },

    isUploadVisible(docType) {
      // edição/reenvio de um documento já existente é feita só pela listagem (modal)
      return !this.getExistingDocument(docType);
    },

    async handleDocumentSubmit(docType, payload) {
      try {
        await this.createDocument({ document_type: docType, ...payload });

        swal.fire({
          title: "Documento enviado!",
          text: "Seu documento foi enviado com sucesso e está em análise.",
          icon: "success",
          confirmButtonText: "OK"
        });

        this.fetchDocuments().catch(() => {});
      } catch (error) {
        const msg =
          error.response && error.response.data && error.response.data.errors
            ? Object.values(error.response.data.errors)
                .flat()
                .join(", ")
            : "Ocorreu um erro ao enviar o documento. Tente novamente.";

        swal.fire({
          title: "Erro",
          text: msg,
          icon: "error",
          confirmButtonText: "OK"
        });
      }
    }
  },
  computed: {
    currentUser() {
      return this.$store.state.user.currentUser;
    },
    documentTypes() {
      const required = this.$store.getters[
        "doctorDocument/requiredDocumentTypes"
      ];
      const optional = this.$store.getters[
        "doctorDocument/optionalDocumentTypes"
      ];
      const visible = [...required, ...optional];
      return this.allDocumentTypes.filter(d => visible.includes(d.type));
    },
    requiredDocumentsCount() {
      return this.$store.getters["doctorDocument/requiredDocumentTypes"].length;
    },
    documents() {
      try {
        return this.$store.getters["doctorDocument/documents"] || [];
      } catch {
        return [];
      }
    },
    loading() {
      try {
        return this.$store.getters["doctorDocument/loading"] || false;
      } catch {
        return false;
      }
    },
    sentDocumentsCount() {
      return this.$store.getters["doctorDocument/sentDocumentsCount"] || 0;
    },
    progressPercentage() {
      if (this.requiredDocumentsCount === 0) return 0;
      return Math.round(
        (this.sentDocumentsCount / this.requiredDocumentsCount) * 100
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.vm-logo {
  display: block;
  height: 22px;
  width: auto;
}

.dd-page {
  min-height: 100vh;
  background: var(--color-bg-page);
  display: flex;
  flex-direction: column;
}

.dd-container {
  flex: 1;
  max-width: 860px;
  margin: 0 auto;
  padding: var(--space-8) var(--space-5) var(--space-10);
  width: 100%;
}

// ── Header ────────────────────────────────────────────────
.dd-back {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-bottom: var(--space-6);
  padding-left: 0;
}

.dd-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);
  margin: 0 0 var(--space-2);
}

.dd-sub {
  margin: 0 0 var(--space-5);
}

// ── Progress ──────────────────────────────────────────────
.dd-progress {
  display: flex;
  align-items: center;
  gap: var(--space-4);

  &__bar {
    flex: 1;
    height: 6px;
    background: var(--color-gray-200);
    border-radius: 99px;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    background: var(--color-primary);
    border-radius: 99px;
    transition: width 0.3s ease;
  }

  &__label {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-500);
    white-space: nowrap;
  }
}

// ── Grid de cards ─────────────────────────────────────────
.dd-grid {
  margin-top: var(--space-8);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);
  align-items: start;
}

.dd-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);

  &--done {
    opacity: 0.75;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-3);
  }

  &__label {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-800);
    line-height: 1.4;
  }
}

// ── Rejection notice ──────────────────────────────────────
.dd-rejection {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-danger-light);
  color: var(--color-danger);
  font-size: var(--font-size-xs);
  line-height: 1.5;

  svg {
    flex: none;
    margin-top: 1px;
  }
}

// ── Footer ────────────────────────────────────────────────
.dd-footer {
  margin-top: var(--space-10);
  display: flex;
  justify-content: center;
}

// ── Responsivo ────────────────────────────────────────────
@media (max-width: 600px) {
  .dd-grid {
    grid-template-columns: 1fr;
  }
}
</style>
